import React, { forwardRef, type ReactElement, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import { DatePicker } from '@atlaskit/datetime-picker';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Popup } from '@atlaskit/popup/popup';
import type { PopupComponentProps } from '@atlaskit/popup/types';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { CUSTOM_OPTION_VALUE } from '../../../constants/filters/types';
import { FilterButton } from '../filter-button';
import { MaybeHoverOutWrapper } from '../hover-out-wrapper';
import DateOption from '../last-modified-filter/date-option';
import { type SelectFilterOption } from '../select-filter';
import { type CustomPopperModifier, matchReferenceWidthModifier } from '../utils/popper';

import { i18n } from './messages';

const styles = cssMap({
	container: {
		boxSizing: 'border-box',
		width: '375px',
		overflow: 'visible',
		backgroundColor: token('elevation.surface.overlay'),
		'&:focus': {
			outline: 'none',
		},
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.overlay'),
		zIndex: 100,
	},
});

const PopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => (
	<Box
		{...props}
		paddingBlock="space.100"
		ref={ref}
		xcss={styles.container}
		data-testid={undefined}
	/>
));

const formatDate = (dateString: string, locale: string): string => {
	const modifiedDateString = dateString.concat('T00:00');
	return new Date(modifiedDateString).toLocaleDateString(locale);
};

const getButtonLabel = (
	isSelected: boolean,
	isCustomDateSelected: boolean,
	selectedLabel: string,
	label: string,
	locale: string,
	closeDateVal?: string,
): string => {
	if (!isSelected || !selectedLabel) {
		return label;
	}

	const prefix = label.length > 0 ? `${label}: ` : '';

	if (isCustomDateSelected && closeDateVal) {
		const valueLabel = formatDate(closeDateVal, locale);
		return `${prefix}${valueLabel}`;
	}

	return `${prefix}${selectedLabel}`;
};

type CloseDateFilterWithDatePickerProps = {
	options: SelectFilterOption[];
	onDropdownOpen?: () => void;
	onSelectChange: (option: SelectFilterOption, customValue: Record<string, string>) => void;
	selectedOptions: SelectFilterOption[];
	customDateValue: string;
	label: string;
	testId?: string;
	isFilterInDropdown?: boolean;
	isDefaultOpen?: boolean;
	iconBefore?: ReactElement | null;
	hideOnHoverOut?: boolean;
	matchReferenceWidth?: boolean;
	contentFit?: 'default' | 'compact';
};

const CloseDateFilterWithDatePicker = ({
	options,
	onDropdownOpen,
	onSelectChange,
	selectedOptions,
	customDateValue,
	label,
	testId,
	isFilterInDropdown,
	isDefaultOpen = false,
	iconBefore,
	hideOnHoverOut,
	matchReferenceWidth = isFilterInDropdown,
	contentFit,
}: CloseDateFilterWithDatePickerProps): React.JSX.Element => {
	const [open, setOpen] = useState(isDefaultOpen);
	const [customDateOpen, setCustomDateOpen] = useState(false);
	const { formatMessage, locale } = useIntl();
	const [focusedOption, setFocusedOption] = useState(0);
	const [mouseHovered, setMouseHovered] = useState(false);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const [selectedDate, setSelectedDate] = useState<string>(customDateValue);

	const isSelected = selectedOptions.length > 0 || open;
	const firstSelected = selectedOptions[0];
	const isCustomDateSelected = firstSelected?.value === CUSTOM_OPTION_VALUE;

	const customModifiers: CustomPopperModifier[] = [
		...(matchReferenceWidth ? [matchReferenceWidthModifier] : []),
	];

	const labelToDisplay = getButtonLabel(
		isSelected,
		isCustomDateSelected,
		firstSelected?.label || '',
		label,
		locale,
		customDateValue,
	);

	const handleOptionClick = (option: SelectFilterOption) => {
		if (option.value === CUSTOM_OPTION_VALUE) {
			setCustomDateOpen(true);
		} else {
			setCustomDateOpen(false);
			onSelectChange(option, { closeDateVal: '' });
			setOpen(false);
		}
	};

	const handleDateApply = () => {
		if (!selectedDate) {
			return;
		}
		const option = options.find((o) => o.value === CUSTOM_OPTION_VALUE);
		if (!option) {
			return;
		}
		onSelectChange(option, { closeDateVal: selectedDate });
		setOpen(false);
	};

	const handleClickDropdownBtn = () => {
		if (!open && onDropdownOpen) {
			onDropdownOpen();
		}
		setOpen(!open);
		setFocusedOption(0);
		buttonRef.current?.focus();
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setFocusedOption((prev) => (prev + 1) % options.length);
		}
		if (event.key === 'ArrowUp') {
			event.preventDefault();
			setFocusedOption((prev) => (prev - 1 + options.length) % options.length);
		}
		if (event.key === 'Enter' && open) {
			event.preventDefault();
			handleOptionClick(options[focusedOption]);
		}
	};

	useEffect(() => {
		if (isCustomDateSelected) {
			setCustomDateOpen(true);
		} else {
			setCustomDateOpen(false);
		}
	}, [isCustomDateSelected]);

	// Get today's date in ISO format for maxDate
	const today = new Date().toISOString().split('T')[0];

	return (
		<MaybeHoverOutWrapper enabled={hideOnHoverOut} callback={() => setOpen(false)}>
			<Popup
				placement="bottom-start"
				onClose={() => setOpen(false)}
				isOpen={open}
				autoFocus={false}
				popupComponent={PopupComponent}
				zIndex={fg('platform-dst-motion-uplift-popup') ? 100 : undefined}
				shouldRenderToParent
				content={() => (
					<Box
						role="menu"
						onMouseOver={() => setMouseHovered(true)}
						onMouseOut={() => setMouseHovered(false)}
					>
						{options.map((option, index) => (
							<DateOption
								hovered={!mouseHovered && focusedOption === index}
								key={option.value}
								option={option}
								onClick={() => handleOptionClick(option)}
							/>
						))}
						{customDateOpen && (
							<Box
								paddingInline="space.150"
								paddingBlock="space.075"
								testId={testId ? `${testId}-date-picker` : undefined}
							>
								<Stack space="space.150">
									<DatePicker
										onChange={(date) => setSelectedDate(date)}
										defaultValue={customDateValue || undefined}
										placeholder={formatMessage(i18n.closeDateFilterDatePlaceholder)}
										locale={locale}
										label={formatMessage(i18n.closeDateFilterDateLabel)}
										shouldShowCalendarButton
										maxDate={today}
									/>
									<Box>
										<Button isDisabled={!selectedDate} onClick={handleDateApply}>
											{formatMessage(i18n.closeDateFilterUpdateButton)}
										</Button>
									</Box>
								</Stack>
							</Box>
						)}
					</Box>
				)}
				trigger={(triggerProps) => (
					<FilterButton
						{...triggerProps}
						contentFit={contentFit}
						testId={testId ? `${testId}-button` : undefined}
						isSelected={isSelected}
						onClick={handleClickDropdownBtn}
						onKeyDown={handleKeyDown}
						filterOrControlId="closeDate"
						isFilterInDropdown={isFilterInDropdown}
						iconBefore={iconBefore}
					>
						{labelToDisplay}
					</FilterButton>
				)}
				modifiers={customModifiers}
			/>
		</MaybeHoverOutWrapper>
	);
};

export default CloseDateFilterWithDatePicker;

export { formatDate, getButtonLabel };
